import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import '../styles/components/article-detail.scss';
import { useNews, Article } from '../context/NewsContext';
import { baseURLnoapi } from '../Constants';
import { analytics } from '../services/analytics';

const ArticleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { getArticleById } = useNews();
  
  useEffect(() => {
    // Fetch the article from the API using the context
    const fetchArticle = async () => {
      setLoading(true);
      
      if (id) {
        try {
          const fetchedArticle = await getArticleById(id);
          
          if (fetchedArticle) {
            setArticle(fetchedArticle);
            // Set page title
            document.title = `${fetchedArticle.title} - Northern Championships Series`;
            // Track article view
            analytics.content.articleViewed(
              fetchedArticle.id,
              fetchedArticle.category,
              fetchedArticle.title
            );
          }
        } catch (error) {
          console.error('Error fetching article:', error);
        }
      }
      
      setLoading(false);
    };
    
    fetchArticle();
    
    // Scroll to top on page load
    window.scrollTo(0, 0);
  }, [id, getArticleById]);

  if (loading) {
    return (
      <div className="article-detail loading">
        <div className="container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="article-detail not-found">
        <div className="container">
          <h1>ARTICLE NOT FOUND</h1>
          <p>Sorry, we couldn't find the article you were looking for.</p>
          <Link to="/news" className="back-button">Return to News</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="article-detail">
      <div className="article-hero" style={{ 
        backgroundImage: `url(${article.imageUrl.startsWith('http') 
          ? article.imageUrl 
          : `${baseURLnoapi}${article.imageUrl}`})` 
      }}>

        <div className="overlay"></div>
        <div className="container">
          <div className="article-meta">
            <span className="article-category">{article.category}</span>
            <span className="article-date">
              {new Date(article.date).toLocaleDateString()} 
              {article.author && `• By ${article.author.displayName || 'Unknown'}`}
            </span>
          </div>
          <h1 className="article-title">{article.title}</h1>
        </div>
      </div>
      
      <div className="container">
        <div className="article-content">
          <div 
            className="content" 
            dangerouslySetInnerHTML={{ __html: article.content || `<p>${article.excerpt}</p>` }}
          />
          
          {article.tags && article.tags.length > 0 && (
            <div className="article-tags">
              <span className="tags-label">Tags:</span>
              {article.tags.map((tag, index) => (
                <Link key={index} to={`/news?tag=${tag.toLowerCase().replace(/\s+/g, '-')}`} className="tag">
                  {tag}
                </Link>
              ))}
            </div>
          )}
          
          <div className="article-navigation">
            <Link to="/news" className="back-button">
              Back to News
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleDetailPage;